import { useGetQuizAttempt, useGradeQuizAttempt } from '@/api/content';
import { useGetFullChildProfile, useGetProfile } from '@/api/user';
import {
  failCriticalThinkingQuizSvg,
  passCriticalThinkingQuizSvg,
} from '@/assets/svgs/student-stats';
import { POrgPageContainer } from '@/components/container';
import SEO from '@/components/seo';
import { BackButton } from '@/components/UI/chakra/backButton';
import { CustomH1, CustomH2 } from '@/components/UI/chakra/forms/texts';
import { PageLoader } from '@/components/UI/chakra/loaders';
import { useUserType } from '@/hooks/useUserType';
import { defaultPictureUrl } from '@/utils/constants';
import {
  Badge,
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Image,
  Stack,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const StudentProfile = () => {
  const { theme } = useUserType();
  const { studentId, quizAttemptId } = useParams();

  const {
    data: { data: user } = { data: undefined },
    isLoading: isFetchingProfile,
  } = useGetProfile();

  const {
    data: { data: child } = { data: undefined },
    isLoading: isFetchingChildProfile,
  } = useGetFullChildProfile(studentId);

  const {
    data: { data: quizAttempt } = { data: undefined },
    isLoading: isFetchingQuizAttempt,
  } = useGetQuizAttempt(quizAttemptId);

  const { isLoading: isSubmittingGrade, mutate: submitGrade } =
    useGradeQuizAttempt(quizAttemptId || '');

  const [mark, setMark] = useState<Record<string, boolean | undefined>>({});

  useEffect(() => {
    setMark(
      Object.fromEntries(
        (quizAttempt?.answers ?? []).map((value, index) => [
          index.toString(),
          value.isCorrect === null ? undefined : value.isCorrect,
        ]),
      ),
    );
  }, [quizAttempt?.answers]);

  const age =
    new Date().getFullYear() -
    new Date(child?.profile?.dateOfBirth || '').getFullYear();

  return (
    <POrgPageContainer>
      <SEO title="Child/Device Profile - GrandmaWura's Hub" />

      <HStack
        w={'full'}
        justifyContent={'space-between'}
        mb={6}
        pb={4}
        borderBottom={'2px solid'}
        borderBottomColor={theme.border100}
      >
        <CustomH1>Quiz Attempt</CustomH1>
      </HStack>

      <HStack w={'100%'} justifyContent={'space-between'}>
        <BackButton />
      </HStack>

      {isFetchingChildProfile || isFetchingProfile || isFetchingQuizAttempt ? (
        <PageLoader />
      ) : (
        <>
          <Box mb={6} mt={6}>
            <Grid
              templateColumns={{
                base: '70px 1fr',
                sm: '105px 1fr',
                md: '105px 1fr 1fr',
              }}
              columnGap={{ base: 4, sm: 6 }}
              rowGap={6}
            >
              <GridItem>
                <Box
                  bg="#B15EA40A"
                  color={theme.text}
                  w={{ base: '70px', sm: '102px' }}
                  h={{ base: '70px', sm: '102px' }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="full"
                  overflow="hidden"
                  border="2px solid"
                  borderColor="#B15EA433"
                >
                  <Image
                    src={child?.profile?.pictureUrl ?? defaultPictureUrl}
                    w="100%"
                    h="100%"
                    alt="Profile picture"
                    objectFit="cover"
                  />
                </Box>
              </GridItem>

              <GridItem>
                <VStack alignItems={'flex-start'} spacing={4}>
                  <ChildLabel
                    header="Name"
                    title={
                      (child?.profile?.firstName?.toUpperCase() || '-') +
                      ' ' +
                      (child?.profile?.lastName?.toUpperCase() || '-')
                    }
                  />
                  <ChildLabel header="Age" title={age} />
                </VStack>
              </GridItem>

              <GridItem ml={{ base: '86px', sm: '129px', md: '0px' }}>
                <VStack alignItems={'flex-start'} spacing={4}>
                  <ChildLabel
                    header="Current Assigned Subscription"
                    comp={
                      <Badge
                        colorScheme={
                          user?.activeSubscription?.subscriptionPlan?.title
                            ? 'purple'
                            : 'none'
                        }
                        px={4}
                        py={1}
                        size={'lg'}
                      >
                        {user?.activeSubscription?.subscriptionPlan?.title ??
                          'none'}
                      </Badge>
                    }
                  />
                  <ChildLabel
                    header="Quiz Score"
                    title={quizAttempt?.score ?? '-'}
                  ></ChildLabel>
                </VStack>
              </GridItem>
            </Grid>
          </Box>

          <Box pb={10} mb={6}>
            <HStack mb={6} px={{ base: 3, sm: 6 }} py={4} bg={theme.bg}>
              <CustomH2>Critical Thinking Answers</CustomH2>
            </HStack>
            <Box>
              {quizAttempt?.answers?.length ? (
                <Stack
                  w={'100%'}
                  mx={'auto'}
                  flexDirection={'row'}
                  flexWrap={'wrap'}
                  columnGap={20}
                  rowGap={'28px'}
                  align={'left'}
                >
                  {quizAttempt?.answers.map((x, index) => (
                    <QuizAttemptBox
                      key={index}
                      index={index + 1}
                      question={x.question}
                      answer={x.answer}
                      isPass={mark[index.toString()]}
                      onPass={() => {
                        setMark((prev) => ({
                          ...prev,
                          [index.toString()]: true,
                        }));
                      }}
                      onFail={() => {
                        setMark((prev) => ({
                          ...prev,
                          [index.toString()]: false,
                        }));
                      }}
                    />
                  ))}
                  <Button
                    mt={'24px'}
                    minW="150px"
                    variant="org"
                    size="lg"
                    fontSize="16px"
                    onClick={() => {
                      if (
                        quizAttempt.answers.length !== Object.keys(mark).length
                      ) {
                        toast.error('Please grade all answers');
                        return;
                      }

                      submitGrade({
                        body: {
                          grades: Object.keys(mark)
                            .map((key) => mark[key])
                            .filter((x) => x !== undefined),
                        },
                      });
                    }}
                    borderRadius={'8px'}
                    isLoading={isSubmittingGrade}
                  >
                    Finish Grading
                  </Button>
                </Stack>
              ) : (
                <Text>No answers found</Text>
              )}
            </Box>
          </Box>
        </>
      )}
    </POrgPageContainer>
  );
};

const ChildLabel = ({
  header,
  title,
  comp,
  textProps,
}: {
  header: string;
  title?: string | number;
  comp?: ReactNode;
  textProps?: TextProps;
}) => {
  return (
    <Box w={'100%'}>
      <Text
        mb={1}
        as={'span'}
        textStyle={'medium'}
        fontWeight={600}
        color={'text.300'}
        whiteSpace={'nowrap'}
      >
        {header}:
      </Text>
      {title ? (
        <Text
          textStyle={'header1'}
          fontWeight={700}
          wordBreak={'break-word'}
          whiteSpace={'preserve-breaks'}
          {...textProps}
        >
          {title}
        </Text>
      ) : null}
      {comp ? <Box pt={1.5}>{comp}</Box> : null}
    </Box>
  );
};

export default StudentProfile;

function QuizAttemptBox({
  index,
  question,
  answer,
  isPass,
  onFail,
  onPass,
}: {
  index: number;
  question: string;
  answer: string;
  isPass?: boolean;
  onFail: () => void;
  onPass: () => void;
}) {
  function getChosenOpacityValue(value: 'pass' | 'fail', isPass?: boolean) {
    if (isPass === undefined) {
      return '100%';
    } else if (isPass === true) {
      return value === 'pass' ? '100%' : '20%';
    } else if (isPass === false) {
      return value === 'fail' ? '100%' : '20%';
    }
  }

  return (
    <VStack width={'100%'} align={'left'}>
      <HStack>
        <Text>{index.toString()}.</Text> <Text>{question}</Text>
      </HStack>
      <HStack columnGap={{ base: '20px', sm: '32px', md: '48px' }}>
        <Box
          width={'70%'}
          height={'72px'}
          background={'#EDF8FD'}
          borderRadius={'8px'}
          padding={'8px'}
          borderWidth={'2.5px'}
        >
          <Text fontSize={'16px'} opacity={'50%'}>
            {answer}
          </Text>
        </Box>
        <HStack columnGap={'16px'}>
          <Image
            alt={'pass'}
            src={passCriticalThinkingQuizSvg}
            w={{ base: '24px', sm: '32px', md: '48px' }}
            objectFit={'contain'}
            _hover={{ opacity: '70%' }}
            opacity={getChosenOpacityValue('pass', isPass)}
            role={'button'}
            onClick={() => {
              onPass();
            }}
          />
          <Image
            alt={'fail'}
            src={failCriticalThinkingQuizSvg}
            w={{ base: '24px', sm: '32px', md: '48px' }}
            _hover={{ opacity: '70%' }}
            role={'button'}
            objectFit={'contain'}
            opacity={getChosenOpacityValue('fail', isPass)}
            onClick={() => {
              onFail();
            }}
          />
        </HStack>
      </HStack>
    </VStack>
  );
}
